import { useCallback, useEffect, useState } from 'react';
// @mui
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
// components
import { fileFormat } from 'src/components/file-thumbnail';
import { useSettingsContext } from 'src/components/settings';
import { getComparator, useTable } from 'src/components/table';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
import { getReports } from 'src/redux/slices/report';
// types
import { IFileFilters, IFileFilterValue } from 'src/types/file';
//
import ReportsFilters from '../reports-filters';
import ReportsGridView from '../reports-grid-view';
import ReportFiltersResult from '../reports-filters-result';
import { useLocales } from '../../../locales';
import { IReport, IReportFilters } from '../../../types/report';

// ----------------------------------------------------------------------

const defaultFilters: IFileFilters = {
  name: '',
  type: [],
  startDate: null,
  endDate: null,
};

// ----------------------------------------------------------------------

export default function ReportListView() {
  const table = useTable({ defaultRowsPerPage: 10 });
  const { t } = useLocales();
  const settings = useSettingsContext();

  const dispatch = useDispatch();

  const reports = useSelector((state) => state.report.reports);

  useEffect(() => {
    dispatch(getReports());
  }, [dispatch]);

  const [filters, setFilters] = useState(defaultFilters);

  const dateError =
    filters.startDate && filters.endDate
      ? filters.startDate.toMillis() > filters.endDate.toMillis()
      : false;

  const dataFiltered = applyFilter({
    inputData: reports as any,
    comparator: getComparator(table.order, table.orderBy),
    filters,
    dateError,
  });

  const canReset =
    !!filters.name || !!filters.type.length || (!!filters.startDate && !!filters.endDate);

  const handleFilters = useCallback(
    (name: string, value: IFileFilterValue) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const renderFilters = (
    <Stack
      spacing={2}
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'flex-end', md: 'center' }}
    >
      <ReportsFilters
        //
        filters={filters}
        onFilters={handleFilters}
      />
    </Stack>
  );

  const renderResults = (
    <ReportFiltersResult
      filters={filters}
      onResetFilters={handleResetFilters}
      //
      canReset={canReset}
      onFilters={handleFilters}
      //
      results={dataFiltered.length}
    />
  );

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <CustomBreadcrumbs
        heading={t('Reports')}
        links={[
          { name: t('Dashboard'), href: paths.dashboard.root },
          {
            name: t('Reports'),
            href: paths.report.root,
          },
          { name: t('List') },
        ]}
        action={
          <Button
            component={RouterLink}
            href={paths.report.new}
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            {t('Add report')}
          </Button>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <Stack
        spacing={2.5}
        sx={{
          my: { xs: 3, md: 5 },
        }}
      >
        {renderFilters}

        {canReset && renderResults}
      </Stack>

      <ReportsGridView table={table} data={reports as any} dataFiltered={dataFiltered} />
    </Container>
  );
}

// ----------------------------------------------------------------------

function applyFilter({
  inputData,
  comparator,
  filters,
}: {
  inputData: IReport[];
  comparator: (a: any, b: any) => number;
  filters: IReportFilters;
  dateError: boolean;
}) {
  const { name, type } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (name) {
    inputData = inputData.filter(
      (file) => file.name.toLowerCase().indexOf(name.toLowerCase()) !== -1
    );
  }

  if (type.length) {
    inputData = inputData.filter((file) => type.includes(fileFormat(file.type)));
  }

  return inputData;
}
